import { FC, PropsWithChildren, useMemo, useState } from "react";
import pluralize from "pluralize";

import { ConfirmationDialog, Paragraph } from "@hightouch/htui";
import { TraitCondition as TraitDefinitionCondition } from "@hightouch/lib/query/visual/types/trait-definitions";
import { BaseQueryBuilderProps } from "src/components/explore/query-builder";

import { QueryBuilderContext, SelectedCondition } from "./hooks";
import { To, useNavigate } from "src/router";
import { EventTraitEnrichmentEntrypoint } from "src/components/audiences/event-trait-enrichment-entrypoint";
import { CreateTraitModal } from "src/components/audiences/create-trait-modal";
import {
  AdditionalColumn,
  ConditionType,
  EventCondition,
  isTraitCondition,
  NumberOfCondition,
  PropertyCondition,
} from "src/types/visual";
import { toSingleCondition } from "src/components/audiences/utils";
import { CreateTraitEnrichmentModal } from "src/components/audiences/create-trait-enrichment-modal";
import { useAddAdditionalColumn } from "src/components/audiences/trait-enrichments/utils";
import { FilterableOptionsProvider } from "src/components/explore/filter-popover/filterable-options-context";
import { getRootFilterSections } from "src/components/explore/filter-popover/constants";
import { useEntitlements } from "src/hooks/use-entitlement";

export type QueryBuilderProviderProps =
  PropsWithChildren<BaseQueryBuilderProps>;

type Requirement = {
  condition: "related model" | "event" | "audience";
  to: To;
};

export const QueryBuilderProvider: FC<Readonly<QueryBuilderProviderProps>> = ({
  audience,
  children,
  parentModel,
  enableReferencingEntryEvent = false,
  enableRealtime = false,
  enableNestedFiltering,
  selectedEvent = null,
  maxNestingDepth = 2,
  variant = "default",
  restrictions,
  visualQueryFilter,
  propertiesOnly = false,
  filterableColumnsOverride,
  staleWarehouseConditions,
}) => {
  const navigate = useNavigate();
  const { data: entitlementsData } = useEntitlements(false);

  const [_selectedCondition, setSelectedCondition] =
    useState<SelectedCondition>(null);

  const selectedCondition = _selectedCondition?.condition;
  const updateSelectedCondition = _selectedCondition?.update;

  const [missingRequirement, setMissingRequirement] = useState<
    Requirement | undefined
  >();

  const addAdditionalColumn = useAddAdditionalColumn();
  const handleAddAdditionalColumn = async (
    additionalColumn: AdditionalColumn,
  ) => {
    if (!audience) return;
    await addAdditionalColumn({ audience, additionalColumn });
  };

  const isTrait = selectedCondition && isTraitCondition(selectedCondition);
  const selectedTrait =
    isTrait &&
    parentModel?.traits?.find(
      (t) => t.id === selectedCondition.property?.column.traitDefinitionId,
    );

  const isTraitTemplate =
    selectedTrait &&
    selectedTrait.is_template &&
    selectedTrait &&
    isTraitCondition(selectedCondition);
  const isRelatedModelCondition =
    !isTraitTemplate && selectedCondition?.type !== ConditionType.Event;
  const isEventCondition =
    !isTraitTemplate && selectedCondition?.type === ConditionType.Event;

  const onTraitCreated = async (conditionWithTrait: PropertyCondition) => {
    // Update the condition with the new trait
    updateSelectedCondition?.(() => conditionWithTrait);
  };

  const showCreateTraitModal =
    selectedCondition?.type === ConditionType.Property &&
    selectedCondition?.propertyOptions?.traitType === "inline_trait";

  // Memoize the context value so consumers downstream
  // (`useQueryBuilderContext()`) don't re-render on every parent render.
  const contextValue = useMemo(
    () => ({
      rootModelId: parentModel?.id ?? null,
      parentModelSemanticType: parentModel?.semantic_type ?? null,
      selectedEvent,
      selectedCondition: _selectedCondition,
      selectCondition: setSelectedCondition,
      audienceReferenceStats: parentModel?.audience_reference_stats, // This might need to move, but let's assume it's root-level for now
      maxNestingDepth,
      variant,
      enableReferencingEntryEvent,
      enableNestedFiltering,
      restrictions,
      filterableColumnsOverride,
      enableRealtime, // Pass this through for 'useFilterableOptions'
      audienceId: audience?.id,
      staleWarehouseConditions: staleWarehouseConditions ?? new Map(),
    }),
    [
      parentModel?.id,
      parentModel?.semantic_type,
      parentModel?.audience_reference_stats,
      selectedEvent,
      _selectedCondition,
      maxNestingDepth,
      variant,
      enableReferencingEntryEvent,
      enableNestedFiltering,
      restrictions,
      filterableColumnsOverride,
      enableRealtime,
      audience?.id,
      staleWarehouseConditions,
    ],
  );

  // Memoize sections so the FilterableOptionsProvider doesn't see a fresh
  // array reference on every render (its internal useMemo on `sections`
  // would otherwise re-run downstream filtering logic).
  const filterSections = useMemo(
    () =>
      getRootFilterSections({
        showJourneys: entitlementsData.entitlements.journeys,
        restrictions,
        isRealtimeAudience: enableRealtime,
        propertiesOnly,
      }),
    [
      entitlementsData.entitlements.journeys,
      restrictions,
      enableRealtime,
      propertiesOnly,
    ],
  );

  return (
    <QueryBuilderContext.Provider value={contextValue}>
      <FilterableOptionsProvider
        modelId={parentModel?.id}
        sections={filterSections}
        visualQueryFilter={visualQueryFilter ?? audience?.visual_query_filter}
      >
        {children}

        <ConfirmationDialog
          confirmButtonText="Go to set up"
          isOpen={Boolean(missingRequirement)}
          title={`No ${pluralize(
            missingRequirement?.condition ?? "objects",
          )} found`}
          variant="warning"
          onClose={() => setMissingRequirement(undefined)}
          onConfirm={() => navigate(missingRequirement?.to ?? "")}
        >
          <Paragraph>
            In order to use this condition you need to set up{" "}
            {missingRequirement?.condition === "event" ? "an" : "a"}{" "}
            {missingRequirement?.condition}. Would you like to create one now?
          </Paragraph>
        </ConfirmationDialog>

        {selectedCondition ? (
          isTraitTemplate ? (
            <CreateTraitEnrichmentModal
              parentModel={parentModel}
              conditions={
                (toSingleCondition(
                  selectedCondition.property.column.conditions,
                ) ?? []) as TraitDefinitionCondition[]
              }
              trait={selectedTrait}
              onClose={() => setSelectedCondition(null)}
              onSubmit={handleAddAdditionalColumn}
            />
          ) : isRelatedModelCondition ? (
            <CreateTraitEnrichmentModal
              parentModel={parentModel}
              filterByRelationshipId={(
                selectedCondition as NumberOfCondition
              ).relationshipId?.toString()}
              conditions={
                ((selectedCondition as NumberOfCondition).subconditions ??
                  []) as unknown as TraitDefinitionCondition[]
              }
              onClose={() => setSelectedCondition(null)}
              onSubmit={handleAddAdditionalColumn}
            />
          ) : isEventCondition ? (
            <EventTraitEnrichmentEntrypoint
              condition={selectedCondition as EventCondition}
              parentModel={parentModel}
              onClose={() => setSelectedCondition(null)}
              onSubmit={handleAddAdditionalColumn}
            />
          ) : null
        ) : null}

        {showCreateTraitModal && parentModel && (
          <CreateTraitModal
            condition={selectedCondition}
            parent={parentModel}
            onClose={() => setSelectedCondition(null)}
            onTraitCreated={onTraitCreated}
          />
        )}
      </FilterableOptionsProvider>
    </QueryBuilderContext.Provider>
  );
};
